import { Box, Image, Text } from "@chakra-ui/react";
import { useState, useEffect, useCallback, useRef } from "react";
import facePoster from "../../assets/GameThumbnail_Empty.png";
import getMediaUrl from "../../utilities/getMediaUrl";

function GamePopUp({game, elementRef, isVisible, isPC}){
    const [position, setPosition] = useState({x:0,y:0});
    const [videoLoaded, setVideoLoaded] = useState(false);
    const rafRef = useRef(null);
    const lastUpdateTime = useRef(0);
    const vidLoadStart = useRef(null);
    const THROTTLE_DELAY = 16;
    
    //position of card relative to the position of hovered item
     const updatePosition=useCallback(()=>{
        if(!elementRef?.current) return;
        const rect = elementRef.current.getBoundingClientRect();
           
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;
        const popupHeight = 300;
        const popupWidth = 400;
        const gap = 10;

        const fullVisibility = rect.top >= 0 && rect.bottom <= viewportHeight;
        const partialVisibility = rect.top < viewportHeight&&rect.bottom > 0;

        if(!partialVisibility) return;

        let newPosition = {x: rect.right + gap, y: rect.top};

        if(newPosition.x+popupWidth > viewportWidth){
            newPosition.x = rect.left - 300;
        }
        
        if(!fullVisibility){
            if(rect.top < 0) newPosition.y = gap;
            else if (rect.bottom > viewportHeight) newPosition.y = viewportHeight - popupHeight - gap;
            else if (rect.top + popupHeight > viewportHeight) newPosition.y = viewportHeight - popupHeight - gap;
        } else {
            if (rect.top + popupHeight > viewportHeight) newPosition.y = viewportHeight - popupHeight - gap;
        }

        if (newPosition.y < 0) newPosition.y = gap;
        if (newPosition.x < 0) newPosition.x = gap;
        setPosition(prevPosition => {
            if (prevPosition.x !== newPosition.x || prevPosition.y !== newPosition.y) return newPosition;
            return prevPosition;
        })
    }, [elementRef]);

    

    const throttledUpdatePosition = useCallback(() => {
        const now = Date.now();
        if (now - lastUpdateTime.current < THROTTLE_DELAY) {
            if (rafRef.current) {
                cancelAnimationFrame(rafRef.current);
            }
            rafRef.current = requestAnimationFrame(throttledUpdatePosition);
            return;
        }
        
        lastUpdateTime.current = now;
        updatePosition();
    }, [updatePosition]);

    const handleVideoLoadStart = useCallback(()=>{
        vidLoadStart.current = performance.now();
        console.log(`[Video Load] Video Loading: ${game?.title} `);
    }, [game?.title])

    const handleVideoCanPlay = useCallback(()=>{
        if(vidLoadStart.current){
            const loadTime = performance.now() - vidLoadStart.current;
            console.log(`[Video Load] Video Can Play: ${game?.title} - Load Time: ${loadTime.toFixed(2)}ms`)
        }
        setVideoLoaded(true);
    }, [game?.title])

    const handleVideoPlay = useCallback(()=>{
        if(vidLoadStart.current){
            const loadTime = performance.now() - vidLoadStart.current;
            console.log(`[Video Load] Video Start Play: ${game?.title} - Load Time: ${loadTime.toFixed(2)}ms`)
        }
    }, [game?.title])

    const handleVideoError = useCallback((e) => {
        console.error(`[Video Error] Failed to load video for ${game?.title}`);
        setVideoLoaded(false);
        e.target.style.display = 'none';
    }, [game?.title]);

    useEffect(() => {
        if (isVisible && game?.hover_video?.url) {
            setVideoLoaded(false);
        }
    }, [isVisible, game?.hover_video?.url]);

    useEffect(()=>{
        if(isVisible&&elementRef){
            updatePosition();
            const handleScroll=()=>updatePosition();
            const handleResize=()=>updatePosition();

            window.addEventListener('scroll', handleScroll, {passive: true});
            window.addEventListener('resize', handleResize, {passive: true});

            return()=>{
                window.removeEventListener('scroll', handleScroll);
                window.removeEventListener('resize', handleResize);
                if(rafRef.current){
                    cancelAnimationFrame(rafRef.current);
                }
            };
        };
    }, [isVisible, elementRef, updatePosition, throttledUpdatePosition]);

    if (!isVisible || !game || isPC) return null;
    if (elementRef?.current) {
        const rect = elementRef.current.getBoundingClientRect();
        const partialVisibility = rect.top < window.innerHeight && rect.bottom > 0;
        if (!partialVisibility) return null;
    }

    const hasVideo = game.hover_video?.url;
    const showVideo = hasVideo && videoLoaded;

    return (
        <Box
            position='fixed'
            left={`${position.x}px`}
            top={`${position.y}px`}
            zIndex={98}
            bgColor='#287cc4'
            color='white'
            borderRadius='10px'
            boxShadow='0 5px 32px rgba(0, 0, 0, 0.4)'
            padding='16px'
            minWidth='300px'
            maxWidth='400px'
            border='1px solid'
            borderColor='white'
            pointerEvents='none'
            style={{
                willChange: 'transform',
                backfaceVisibility: 'hidden',
                perspective: '1000px'
            }}
        >
            <Box position="relative">
                {/* Always render the image first */}
                <Image 
                    src={getMediaUrl(game.thumbnail.url)}
                    // src={`https://leaderbored-api.taktylstudios.com${game.thumbnail.url}`} 
                    alt={game.title}
                    borderRadius='6px'
                    marginBottom='10px'
                    width='100%'
                    maxHeight='200px'
                    objectFit='cover'
                    loading='lazy'
                    style={{
                        display: showVideo ? 'none' : 'block'
                    }}
                />
                
                {/* Render video if available, but initially hidden */}
                {hasVideo && (
                    <video
                        src={getMediaUrl(game.hover_video.url)}
                        // src={`https://leaderbored-api.taktylstudios.com${game.hover_video.url}`}
                        alt={game.title}
                        style={{
                            borderRadius:'6px',
                            marginBottom:'10px',
                            width:'100%',
                            maxHeight:'200px',
                            objectFit:'cover',
                            display: showVideo ? 'block' : 'none'
                        }}
                        muted
                        autoPlay
                        loop
                        playsInline
                        preload="metadata"
                        onLoadStart={handleVideoLoadStart}
                        onCanPlay={handleVideoCanPlay}
                        onPlay={handleVideoPlay}
                        onError={handleVideoError}
                        poster={facePoster} // always show face while first frame loads
                    />
                )}
            </Box>
            
            <Box 
                marginBottom='8px' 
                display='flex'
                flexDirection='column'
                alignItems='center'
                justifyContent='center'
            >
                <Text fontSize='xl' fontWeight='bold' marginBottom='8px'>{game.title}</Text>
            </Box>
            
        </Box>
    )
}
export default GamePopUp;